/**
 * GuestLoginBox · iter149
 *
 * Kompakte Loginbox für die rechte Desktop-Spalte (nur Guests).
 * E-Mail + Passwort + Einloggen + PW vergessen + Registrieren-Link.
 * Bei erfolgreichem Login: window.location.reload() damit der Auth-Context
 * die neuen Cookies liest.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function GuestLoginBox({ isDark = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // iter174d · STX-Darkmode-Klassen (Bug-Report 2026-02-28: Login-Widget weiß im STX-Dark).
  const boxCls = isDark ? "bg-stx-panel border-stx-border" : "bg-white border-[#E5E5E5]";
  const labelCls = isDark ? "text-stx-text-muted" : "text-[#666]";
  const inputCls = isDark
    ? "bg-stx-bg border-stx-border text-stx-text placeholder:text-stx-text-muted focus:border-stx-accent"
    : "border-[#E5E5E5] focus:border-[#0A0A0A]";
  const btnCls = isDark
    ? "bg-stx-accent text-stx-accent-fg hover:bg-stx-accent-hover"
    : "bg-[#0A0A0A] text-white hover:bg-[#222]";
  const linkMutedCls = isDark ? "text-stx-text-muted hover:text-stx-accent" : "text-[#666] hover:text-[#0A0A0A]";
  const linkAccentCls = isDark ? "font-semibold text-stx-accent" : "font-semibold text-[#0A0A0A]";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // FIX iter154 · Backend sendet `require_2fa` (ohne 's'). Der alte Code prüfte
      // fälschlich `requires_2fa`/`two_factor_required` — bei 2FA-Accounts (z. B. Admin)
      // fiel er durch und rief window.location.href = "/" ohne gültige Session-Cookies auf,
      // Ergebnis: Reload → wieder Guest.
      if (data?.require_2fa || data?.requires_2fa || data?.two_factor_required) {
        navigate(`/login?email=${encodeURIComponent(email)}&continue=1`);
        return;
      }
      // Erfolgreicher Non-2FA-Login: Cookies wurden vom Server gesetzt → hart neu laden
      window.location.href = "/";
    } catch (err) {
      const d = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setError(t("login.too_many_attempts"));
      } else if (typeof d === "string") {
        setError(d);
      } else if (d?.code === "ACCOUNT_SUSPENDED" || d?.code === "ACCOUNT_DELETED") {
        setError(d.message || "Konto gesperrt oder gelöscht.");
      } else {
        setError(d?.message || "E-Mail oder Passwort ist falsch.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="guest-login-box" data-dark={isDark ? "1" : "0"} className={`rounded-2xl border p-4 ${boxCls}`}>
      <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${labelCls}`}>
        {t("guest_login.title", { defaultValue: "Willkommen zurück" })}
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2">
        <input
          data-testid="guest-login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("guest_login.email", { defaultValue: "E-Mail-Adresse" })}
          className={`h-10 px-3 rounded-full border text-sm focus:outline-none ${inputCls}`}
          autoComplete="email"
          required
        />
        <input
          data-testid="guest-login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("guest_login.password", { defaultValue: "Passwort" })}
          className={`h-10 px-3 rounded-full border text-sm focus:outline-none ${inputCls}`}
          autoComplete="current-password"
          required
        />
        <button
          type="submit"
          data-testid="guest-login-submit"
          disabled={busy}
          className={`h-10 rounded-full text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2 ${btnCls}`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {t("guest_login.submit", { defaultValue: "Einloggen" })}
        </button>
        {error && (
          <p data-testid="guest-login-error" className="text-xs text-red-500 text-center">
            {String(error)}
          </p>
        )}
      </form>
      <div className="flex items-center justify-between text-xs mt-3">
        <button
          type="button"
          data-testid="guest-login-forgot"
          onClick={() => navigate("/forgot-password")}
          className={linkMutedCls}
        >
          {t("guest_login.forgot", { defaultValue: "Passwort vergessen" })}
        </button>
        <button
          type="button"
          data-testid="guest-login-register-link"
          onClick={() => navigate("/register")}
          className={linkAccentCls}
        >
          {t("guest_login.register", { defaultValue: "Registrieren" })}
        </button>
      </div>
    </section>
  );
}
